import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Box,
  Switch,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  searchEmployeesForBranch,
  getEmployeesByBranch,
  addEmployeeToBranch,
  removeEmployeeFromBranch,
} from '../../../api/staff';
import { centersDetail } from '../../../api/store-management';
import { getEmployeePositionsList } from '../../../api/employeePosition';
import { errorMessages } from '../../../utils/methods';
import CommonModalButton from '../../../form-components/CommonModalButton';
import CommonSearchSubmitButton from '../../../form-components/CommonSearchSubmitButton';
import CommonDatePicker from '../../../form-components/CommonDatePicker';
import CommonSelect from '../../../form-components/CommonSelect';
import ConfirmModal from '../../common/modal/ConfirmModal';
import {
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  SearchRow,
  HeaderCell,
  BodyRow,
  BodyCell,
  EmptyRow,
  AddButton,
  RemoveButton,
  SectionTitle,
  InfoText,
} from './EmployeeBranchManagementStyled';

const EmployeeBranchManagement = ({ open, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);
  const branchId = user?.branch?.branchId;
  const branchName = user?.branch?.branchName;
  const token = user?.token;

  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [removeTarget, setRemoveTarget] = useState(null);
  const [showLeft, setShowLeft] = useState(false);
  const [addTarget, setAddTarget] = useState(null);
  const [centerSportTypes, setCenterSportTypes] = useState([]);

  // 센터 종목 목록 조회
  useEffect(() => {
    if (open && user?.branch?.centerId && token) {
      centersDetail(token, user.branch.centerId)
        .then(res => {
          setCenterSportTypes(res?.branchDetailInfo?.sportTypes || []);
        })
        .catch(() => {});
    }
  }, [open, user?.branch?.centerId, token]);
  const [personnelInfo, setPersonnelInfo] = useState({
    hiredDate: '',
    employeeClassification: 'WORKING',
    exposureYn: 'Y',
    employeePositionId: '',
    responsibilities: [],
    sportTypes: [],
  });

  // 직급 목록
  const { data: positionsRaw } = useQuery({
    queryKey: ['employeePositions', user?.headBranch?.headBranchId, branchId],
    queryFn: () => getEmployeePositionsList(token, `headBranchId=${user?.headBranch?.headBranchId}&branchId=${branchId}`),
    enabled: open && !!token && !!user?.headBranch?.headBranchId,
  });
  const positionOptions = (positionsRaw?.content || [])
    .sort((a, b) => (a.employeePositionOrder || 999) - (b.employeePositionOrder || 999))
    .map(p => ({ value: String(p.employeePositionId), label: p.employeePositionName || p.positionName }))
    .filter(p => p.label);

  // 현재 지점 소속 임직원 목록
  const { data: branchEmployees = [] } = useQuery({
    queryKey: ['employee-branches', branchId],
    queryFn: () => getEmployeesByBranch(branchId, token),
    enabled: open && !!branchId && !!token,
  });

  // 임직원 검색 결과
  const { data: searchResults = [] } = useQuery({
    queryKey: ['employee-branch-search', searchKeyword],
    queryFn: () => searchEmployeesForBranch(searchKeyword, token),
    enabled: !!searchKeyword && !!token,
  });

  // 추가
  const addMutation = useMutation({
    mutationFn: employeeId => addEmployeeToBranch({
      employeeId,
      branchId,
      hiredDate: personnelInfo.hiredDate || null,
      employeeClassification: personnelInfo.employeeClassification,
      exposureYn: personnelInfo.exposureYn,
      employeePositionId: personnelInfo.employeePositionId || null,
      responsibilities: personnelInfo.responsibilities.length > 0 ? personnelInfo.responsibilities : null,
      sportTypes: personnelInfo.sportTypes.length > 0 ? personnelInfo.sportTypes : null,
    }, token),
    onSuccess: () => {
      toast.success('임직원이 추가되었습니다');
      queryClient.invalidateQueries({ queryKey: ['employee-branches', branchId] });
      queryClient.invalidateQueries({ queryKey: ['employee-branch-search', searchKeyword] });
    },
  });

  // 제거
  const removeMutation = useMutation({
    mutationFn: id => removeEmployeeFromBranch(id, token),
    onSuccess: () => {
      toast.success('임직원이 제거되었습니다');
      queryClient.invalidateQueries({ queryKey: ['employee-branches', branchId] });
      setRemoveTarget(null);
    },
  });

  const handleSearch = () => {
    if (!keyword.trim()) {
      toast.warning('검색어를 입력해주세요');
      return;
    }
    setSearchKeyword(keyword.trim());
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch();
  };

  // 이미 소속된 임직원인지 확인
  const isAlreadyAdded = employeeId => branchEmployees.some(eb => eb.employeeId === employeeId);

  // 소속 목록 필터 + 정렬 (최근 추가일 기준, 퇴사자 토글)
  const filteredBranchEmployees = useMemo(() => {
    let filtered = [...branchEmployees];
    if (!showLeft) {
      filtered = filtered.filter(eb => eb.classification !== 'LEFT');
    }
    // 최근 추가일 기준 내림차순
    filtered.sort((a, b) => {
      const dateA = a.createdAt || '';
      const dateB = b.createdAt || '';
      return dateB.localeCompare(dateA);
    });
    return filtered;
  }, [branchEmployees, showLeft]);

  const handleClose = () => {
    setKeyword('');
    setSearchKeyword('');
    setShowLeft(false);
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>임직원 등록</ModalTitle>
            <IconButton onClick={handleClose} size="small" sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </ModalHeader>

          <ModalBody>
            {/* 검색 영역 */}
            <SectionTitle>강사 계정 검색</SectionTitle>
            <InfoText>
              * 강사분께 바디스위치 - 강사 앱에서 회원가입을 먼저 진행해달라고 안내해주세요.<br />
              * 회원가입 완료 후 아이디, 이름, 전화번호로 검색하여 추가할 수 있습니다.
            </InfoText>
            <SearchRow>
              <TextField
                size="small"
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="아이디, 이름, 전화번호"
                sx={{ flex: 1 }}
              />
              <CommonSearchSubmitButton onClick={handleSearch}>검색</CommonSearchSubmitButton>
            </SearchRow>

            {/* 검색 결과 */}
            {searchKeyword && (
              <TableContainer sx={{ border: '1px solid #e8e8e8', borderRadius: '8px', marginBottom: '24px' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <HeaderCell align="center">이름</HeaderCell>
                      <HeaderCell align="center">아이디</HeaderCell>
                      <HeaderCell align="center">전화번호</HeaderCell>
                      <HeaderCell align="center" sx={{ width: '80px' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.length > 0 ? (
                      searchResults.map(emp => (
                        <BodyRow key={emp.employeeId}>
                          <BodyCell align="center">{emp.name}</BodyCell>
                          <BodyCell align="center">{emp.username}</BodyCell>
                          <BodyCell align="center">{emp.phoneNumber}</BodyCell>
                          <BodyCell align="center">
                            {isAlreadyAdded(emp.employeeId) ? (
                              <Box sx={{ fontSize: '12px', color: '#999' }}>소속됨</Box>
                            ) : (
                              <AddButton
                                onClick={() => {
                                  setAddTarget(emp);
                                  setPersonnelInfo({ hiredDate: '', employeeClassification: 'WORKING', exposureYn: 'Y', employeePositionId: '', responsibilities: [], sportTypes: [] });
                                }}>
                                추가
                              </AddButton>
                            )}
                          </BodyCell>
                        </BodyRow>
                      ))
                    ) : (
                      <TableRow>
                        <EmptyRow colSpan={4}>검색 결과가 없습니다.</EmptyRow>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* 현재 소속 목록 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '12px' }}>
              <SectionTitle sx={{ mb: 0 }}>{branchName} 소속 임직원</SectionTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ fontSize: '12px', color: '#666' }}>퇴사자 포함</Typography>
                <Switch
                  size="small"
                  checked={showLeft}
                  onChange={e => setShowLeft(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' } }}
                />
              </Box>
            </Box>
            <TableContainer sx={{ border: '1px solid #e8e8e8', borderRadius: '8px', maxHeight: '300px' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <HeaderCell align="center">이름</HeaderCell>
                    <HeaderCell align="center">아이디</HeaderCell>
                    <HeaderCell align="center">전화번호</HeaderCell>
                    <HeaderCell align="center">입사일</HeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBranchEmployees.length > 0 ? (
                    filteredBranchEmployees.map(eb => (
                        <BodyRow key={eb.employeeBranchId}>
                          <BodyCell align="center">{eb.name}</BodyCell>
                          <BodyCell align="center">{eb.username}</BodyCell>
                          <BodyCell align="center">{eb.phoneNumber}</BodyCell>
                          <BodyCell align="center">
                            {eb.hiredDate || '-'}
                          </BodyCell>
                        </BodyRow>
                      ))
                  ) : (
                    <TableRow>
                      <EmptyRow colSpan={4}>소속 임직원이 없습니다.</EmptyRow>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ModalBody>

          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={handleClose}>
              닫기
            </CommonModalButton>
          </ModalFooter>
        </ModalContainer>
      </Modal>

      <ConfirmModal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => removeMutation.mutate(removeTarget.employeeBranchId)}
        message={`${removeTarget?.name} 님을 ${branchName}에서 제거하시겠습니까?`}
        highlightText={removeTarget?.name}
        highlightColor="#EF4444"
        cancelText="취소"
        confirmText="제거"
        confirmColor="#EF4444"
      />

      {/* 인사정보 입력 모달 */}
      <Modal open={!!addTarget} onClose={() => setAddTarget(null)}>
        <ModalContainer sx={{ maxWidth: '500px' }}>
          <ModalHeader>
            <ModalTitle>{addTarget?.name} 인사정보 등록</ModalTitle>
            <IconButton onClick={() => setAddTarget(null)} size="small" sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </ModalHeader>
          <ModalBody>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>입사일 <span style={{ color: '#f43f5e' }}>*</span></Typography>
                <CommonDatePicker
                  value={personnelInfo.hiredDate || null}
                  onChange={date => {
                    if (date && !isNaN(date.getTime())) {
                      const y = date.getFullYear();
                      const m = String(date.getMonth() + 1).padStart(2, '0');
                      const d = String(date.getDate()).padStart(2, '0');
                      setPersonnelInfo(p => ({ ...p, hiredDate: `${y}-${m}-${d}` }));
                    } else {
                      setPersonnelInfo(p => ({ ...p, hiredDate: '' }));
                    }
                  }}
                  sx={{ width: '100%', '& input': { cursor: 'pointer', caretColor: 'transparent' } }}
                  placeholder="입사일을 선택해주세요"
                  openOnClick
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>재직상태 <span style={{ color: '#f43f5e' }}>*</span></Typography>
                <CommonSelect
                  value={personnelInfo.employeeClassification}
                  onChange={e => setPersonnelInfo(p => ({ ...p, employeeClassification: e.target.value }))}
                  width="100%"
                  options={[
                    { value: 'WORKING', label: '재직' },
                    { value: 'LEFT', label: '퇴사' },
                  ]}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>회원앱 노출 여부 <span style={{ color: '#f43f5e' }}>*</span></Typography>
                <CommonSelect
                  value={personnelInfo.exposureYn}
                  onChange={e => setPersonnelInfo(p => ({ ...p, exposureYn: e.target.value }))}
                  width="100%"
                  options={[
                    { value: 'Y', label: '노출' },
                    { value: 'N', label: '비노출' },
                  ]}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>직급</Typography>
                <CommonSelect
                  value={personnelInfo.employeePositionId}
                  onChange={e => setPersonnelInfo(p => ({ ...p, employeePositionId: e.target.value }))}
                  width="100%"
                  emptyLabel="선택 안 함"
                  options={positionOptions}
                />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>담당업무 <span style={{ color: '#f43f5e' }}>*</span></Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['PRIVATE_LESSON', 'GROUP_LESSON', 'CUSTOMER_MANAGEMENT', 'STORE_MANAGEMENT'].map(r => (
                    <Box
                      key={r}
                      onClick={() => {
                        setPersonnelInfo(p => ({
                          ...p,
                          responsibilities: p.responsibilities.includes(r)
                            ? p.responsibilities.filter(v => v !== r)
                            : [...p.responsibilities, r],
                        }));
                      }}
                      sx={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: personnelInfo.responsibilities.includes(r) ? '#4AB3BC' : '#ddd',
                        color: personnelInfo.responsibilities.includes(r) ? '#4AB3BC' : '#666',
                        backgroundColor: personnelInfo.responsibilities.includes(r) ? '#f0fafa' : '#fff',
                      }}>
                      {{ PRIVATE_LESSON: '개인레슨', GROUP_LESSON: '그룹레슨', CUSTOMER_MANAGEMENT: '고객관리', STORE_MANAGEMENT: '매장관리' }[r]}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: '6px' }}>종목 <span style={{ color: '#f43f5e' }}>*</span></Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {centerSportTypes.length > 0 ? centerSportTypes.map(s => (
                    <Box
                      key={s}
                      onClick={() => {
                        setPersonnelInfo(p => ({
                          ...p,
                          sportTypes: p.sportTypes.includes(s)
                            ? p.sportTypes.filter(v => v !== s)
                            : [...p.sportTypes, s],
                        }));
                      }}
                      sx={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: personnelInfo.sportTypes.includes(s) ? '#4AB3BC' : '#ddd',
                        color: personnelInfo.sportTypes.includes(s) ? '#4AB3BC' : '#666',
                        backgroundColor: personnelInfo.sportTypes.includes(s) ? '#f0fafa' : '#fff',
                      }}>
                      {{ FITNESS: '피트니스', PILATES: '필라테스', YOGA: '요가', GOLF: '골프', SWIMMING: '수영', DANCE: '댄스', CROSSFIT: '크로스핏', BOXING: '복싱', BALL_GAMES: '구기종목', CYCLE: '사이클', EDUCATION: '교육', BARRE_PILATES: '바레필라테스', TANNING: '태닝', STRETCHING: '스트레칭' }[s] || s}
                    </Box>
                  )) : (
                    <Typography sx={{ fontSize: '12px', color: '#999' }}>등록된 종목이 없습니다.</Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <CommonModalButton variant="cancel" onClick={() => setAddTarget(null)}>
              취소
            </CommonModalButton>
            <CommonModalButton
              variant="submit"
              disabled={addMutation.isPending}
              onClick={() => {
                if (!personnelInfo.hiredDate) { toast.error('입사일을 입력해주세요'); return; }
                if (!personnelInfo.employeeClassification) { toast.error('재직상태를 선택해주세요'); return; }
                if (!personnelInfo.exposureYn) { toast.error('회원앱 노출 여부를 선택해주세요'); return; }
                if (personnelInfo.responsibilities.length === 0) { toast.error('담당업무를 선택해주세요'); return; }
                if (personnelInfo.sportTypes.length === 0) { toast.error('종목을 선택해주세요'); return; }
                addMutation.mutate(addTarget.employeeId, {
                  onSuccess: () => setAddTarget(null),
                });
              }}>
              등록
            </CommonModalButton>
          </ModalFooter>
        </ModalContainer>
      </Modal>
    </>
  );
};

export default EmployeeBranchManagement;
